import {
  Alert,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import { useEffect, useState } from "react";
import { adminDataApi, type CaseTaskDto } from "../../services/admin-data-api";

const statusColor: Record<string, "default" | "success" | "warning" | "error"> = {
  OPEN: "warning",
  COMPLETED: "success",
  CANCELLED: "default"
};

const formatDate = (value: string) =>
  new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium"
  }).format(new Date(value));

export const TasksPage = () => {
  const [tasks, setTasks] = useState<CaseTaskDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await adminDataApi.getTasks();
        setTasks(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Unable to load tasks");
      } finally {
        setIsLoading(false);
      }
    };

    load().catch(() => {
      setError("Unable to load tasks");
      setIsLoading(false);
    });
  }, []);

  return (
    <>
      <Typography variant="h4" component="h1" gutterBottom>
        Case Tasks
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Monitor action items across cases and keep workloads on track.
      </Typography>
      {isLoading ? <CircularProgress sx={{ display: "block", my: 4 }} /> : null}
      {error ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      ) : null}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Task</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Due</TableCell>
              <TableCell>Case</TableCell>
              <TableCell>Assigned To</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tasks.map((item) => (
              <TableRow key={item.id} hover>
                <TableCell>{item.title}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={statusColor[item.status] ?? "default"}
                    label={item.status}
                  />
                </TableCell>
                <TableCell>{formatDate(item.dueDate)}</TableCell>
                <TableCell>{item.case?.title ?? "Standalone task"}</TableCell>
                <TableCell>{item.assignedTo?.email ?? "Unassigned"}</TableCell>
              </TableRow>
            ))}
            {!isLoading && tasks.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No tasks found.
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};
